import type {
  ChurnRiskLevel,
  UsageInsight,
  UsageSeatBands,
} from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import {
  PLAY_CHURN_RANK,
  PLAY_TONE_ORDER,
  RENEWAL_SOON_DAYS,
} from "@/v2-backoffice-client/portfolio/portfolio.constants"
import type {
  PortfolioCustomerSummary,
  PortfolioDeadline,
} from "@/v2-backoffice-client/portfolio/portfolio.utils"

/**
 * One entry of the global playbook: a customer-level play (verbatim from
 * the scope derivations) plus everything the list needs to rank and route
 * it — churn level for urgency, deadline for the renewal countdown, and
 * the 12-month billed revenue as the money at stake (seat count as the
 * fallback size read for customers without invoice history).
 */
export type PortfolioPlay = {
  customerExternalId: string
  customerName: string
  kind: UsageInsight["kind"]
  tone: UsageInsight["tone"]
  text: string
  brief: string
  mentions: UsageInsight["mentions"]
  churnLevel: ChurnRiskLevel
  churnReasons: string[]
  deadline: PortfolioDeadline | null
  /** 12-month billed revenue; null without invoice history. */
  billed12M: number | null
  primaryCurrency: string | null
  /** Fenerum's account uuid — the "Open in Fenerum" handle; null when unlinked. */
  billingAccountUuid: string | null
  seatCount: number
  activeSeats: number
  /** Every seat by engagement band, for the card's seat bar. */
  seatBands: UsageSeatBands
  avgScore: number | null
  trendPct: number | null
}

/**
 * Flattens every customer's plays into one list, most urgent first. The
 * order is deliberately explainable, tiered rather than a blended score:
 * 1. tone — actions before opportunities before wins;
 * 2. churn risk — high before medium before the rest;
 * 3. deadline — sooner renewal/trial dates first, none last;
 * 4. money at stake — bigger 12-month books first, no invoice history last;
 * 5. seat count — bigger accounts first;
 * 6. customer name — stable tiebreak.
 */
export function derivePortfolioPlays(
  customers: PortfolioCustomerSummary[],
): PortfolioPlay[] {
  return customers
    .flatMap((customer) =>
      customer.insights.map((insight) => ({
        customerExternalId: customer.externalCustomerId,
        customerName: customer.name,
        kind: insight.kind,
        tone: insight.tone,
        text: insight.text,
        brief: insight.brief,
        mentions: insight.mentions,
        churnLevel: customer.summary.churnRisk.level,
        churnReasons: customer.summary.churnRisk.reasons,
        deadline: customer.deadline,
        billed12M: customer.billed12M,
        primaryCurrency: customer.primaryCurrency,
        billingAccountUuid: customer.billingAccountUuid,
        seatCount: customer.summary.seatCount,
        activeSeats: customer.summary.activeUsers28d,
        seatBands: customer.summary.seatBands,
        avgScore: customer.summary.avgScore,
        trendPct: customer.summary.trendPct,
      })),
    )
    .sort(comparePortfolioPlays)
}

/** One playbook group: a customer with its plays in urgency order. */
export type PortfolioPlayGroup = {
  customerExternalId: string
  customerName: string
  /**
   * The most urgent tone among the account's plays: the chapter the account
   * sits in (something to fix beats an opening beats a win).
   */
  tone: UsageInsight["tone"]
  churnLevel: ChurnRiskLevel
  churnReasons: string[]
  deadline: PortfolioDeadline | null
  /** 12-month billed revenue; null without invoice history. */
  billed12M: number | null
  primaryCurrency: string | null
  /** Fenerum's account uuid — the "Open in Fenerum" handle; null when unlinked. */
  billingAccountUuid: string | null
  seatCount: number
  activeSeats: number
  seatBands: UsageSeatBands
  avgScore: number | null
  trendPct: number | null
  plays: PortfolioPlay[]
}

/**
 * Groups an urgency-sorted play list by customer, preserving the order in
 * which customers first appear — a customer ranks where its most urgent
 * play ranks, so the grouped list reads top-down like the flat one did
 * without repeating the same account three rows in a row.
 */
export function groupPortfolioPlays(
  plays: PortfolioPlay[],
): PortfolioPlayGroup[] {
  const groups: PortfolioPlayGroup[] = []
  const byCustomer = new Map<string, PortfolioPlayGroup>()
  for (const play of plays) {
    let group = byCustomer.get(play.customerExternalId)
    if (!group) {
      group = {
        customerExternalId: play.customerExternalId,
        customerName: play.customerName,
        tone: play.tone,
        churnLevel: play.churnLevel,
        churnReasons: play.churnReasons,
        deadline: play.deadline,
        billed12M: play.billed12M,
        primaryCurrency: play.primaryCurrency,
        billingAccountUuid: play.billingAccountUuid,
        seatCount: play.seatCount,
        activeSeats: play.activeSeats,
        seatBands: play.seatBands,
        avgScore: play.avgScore,
        trendPct: play.trendPct,
        plays: [],
      }
      byCustomer.set(play.customerExternalId, group)
      groups.push(group)
    }
    group.plays.push(play)
  }
  return groups
}

/**
 * Whether an account with an action play is this week's call: high churn
 * risk, or a renewal or trial end inside RENEWAL_SOON_DAYS. The rest of the
 * action accounts are the watch list.
 */
export function isUrgentPlayGroup(group: PortfolioPlayGroup) {
  return (
    group.churnLevel === "high" ||
    (group.deadline != null && group.deadline.daysUntil <= RENEWAL_SOON_DAYS)
  )
}

/** The figures over the playbook: how much of the week it is. */
export type PortfolioPlaybookSummary = {
  /** Accounts with a play to act on that are this week's call. */
  actionAccounts: number
  /** Accounts with a play to act on but time: the watch list. */
  watchAccounts: number
  /** 12-month billed revenue of this week's accounts. */
  actionBilled: number
  currency: string | null
  openings: number
  openingAccounts: number
  wins: number
  winAccounts: number
}

/**
 * Sums the playbook up for its key-figure tiles. Money is nominal across
 * currencies, like the rest of the Portfolio (single-currency reality
 * today).
 */
export function derivePortfolioPlaybookSummary(
  plays: PortfolioPlay[],
): PortfolioPlaybookSummary {
  const summary: PortfolioPlaybookSummary = {
    actionAccounts: 0,
    watchAccounts: 0,
    actionBilled: 0,
    currency: null,
    openings: 0,
    openingAccounts: 0,
    wins: 0,
    winAccounts: 0,
  }
  for (const group of groupPortfolioPlays(plays)) {
    const tones = new Set(group.plays.map((play) => play.tone))
    if (tones.has("action")) {
      if (isUrgentPlayGroup(group)) {
        summary.actionAccounts += 1
        if (group.billed12M != null) {
          summary.actionBilled += group.billed12M
          summary.currency ??= group.primaryCurrency
        }
      } else {
        summary.watchAccounts += 1
      }
    }
    if (tones.has("opportunity")) summary.openingAccounts += 1
    if (tones.has("positive")) summary.winAccounts += 1
  }
  for (const play of plays) {
    if (play.tone === "opportunity") summary.openings += 1
    if (play.tone === "positive") summary.wins += 1
  }
  return summary
}

function comparePortfolioPlays(a: PortfolioPlay, b: PortfolioPlay) {
  const toneDiff = PLAY_TONE_ORDER[a.tone] - PLAY_TONE_ORDER[b.tone]
  if (toneDiff !== 0) return toneDiff
  const churnDiff =
    PLAY_CHURN_RANK[b.churnLevel] - PLAY_CHURN_RANK[a.churnLevel]
  if (churnDiff !== 0) return churnDiff
  const deadlineDiff = deadlineRank(a.deadline) - deadlineRank(b.deadline)
  if (deadlineDiff !== 0) return deadlineDiff
  const moneyDiff = (b.billed12M ?? -1) - (a.billed12M ?? -1)
  if (moneyDiff !== 0) return moneyDiff
  const seatDiff = b.seatCount - a.seatCount
  if (seatDiff !== 0) return seatDiff
  return a.customerName.localeCompare(b.customerName)
}

/** Sooner deadlines rank first; customers without one rank last. */
function deadlineRank(deadline: PortfolioDeadline | null) {
  return deadline?.daysUntil ?? Number.MAX_SAFE_INTEGER
}
